// DOM HUD overlay.
import { FORM_GATES } from '../core/bossForms';
import { BOONS, BoonId, INNATE, MAX_PASSIVES, MAX_WEAPON_LEVEL, PASSIVES, PassiveId, RISK_TRAITS, SEALS_REQUIRED, WEAPONS, WeaponId } from '../core/content';
/** ' risk' for a risk trait card (Overload): the red-violet RISK tint in every card design and theme (style.css .risk) */
const riskCls = (kind: string, id: string): string => (kind === 'passive' && RISK_TRAITS.includes(id as PassiveId) ? ' risk' : '');
import { Choice, Sim } from '../core/sim';
import { HEAL_ICON, fusionIcon, techIcon, traitIcon } from './icons';
import { FUSIONS, isFusionId, recipesWith, type FusionId } from '../core/fusions';
import { fusionBonds, fusionCardIcon, fusionCardNote, fusionPlannerCard, fusionSlot, fusionStatGrid } from './fusionUi'; // SKILL FUSIONS
import { SkillStats, diffStats, fmtRow, passiveStats, skillStats } from '../core/skillStats';
import { FLASH } from '../render/a11y'; // comfort & accessibility: Reduce flashing
import { FURY_TIME } from '../core/pickupsField'; // MAP PICKUPS (owner 2026-11-02)
import { LU_REVEAL_MS, type RevealKind } from './levelUpSeq'; // LEVEL-UP MOMENT
import { APEX_BOONS, endlessMilestoneAt, endlessTierLabel, endlessTierName, roman as romanNum } from '../core/endless'; // ENDLESS GOALS
import { SURGE_FORMS, surgeForm } from '../core/surgeForms'; // SURGE FORMS (owner 2026-11-03): bar colours, labels and the 8 segments

/** Stat grid for a card (owner 2026-11-02: 'give all the usual details a skill card would have'). With `prev`, each row
 *  shows current → next and highlights what improves. */
export function statGrid(next: SkillStats, prev: SkillStats | null = null): string {
  if (!next.rows.length) return '';
  const cells = diffStats(prev, next).map((d) => {
    const now = fmtRow(d.row);
    const val = d.added ? `<b class="up">${now}</b><em>NEW</em>`
      : d.changed && d.prev ? `<s>${fmtRow(d.prev)}</s><span class="ar">→</span><b class="${d.better ? 'up' : 'dn'}">${now}</b>`
      : `<b>${now}</b>`;
    return `<div class="st${d.changed ? ' ch' : ''}"><i>${d.row.label}</i><span class="v">${val}</span></div>`;
  }).join('');
  return `<div class="stats">${cells}</div>${next.note ? `<div class="snote">${next.note}</div>` : ''}`;
}
/** current → next stats for a draft card (null for bonus / restore cards) */
export function cardStats(c: Choice, sim: Sim): string {
  if (c.kind === 'weapon') {
    const id = c.id as WeaponId, cur = sim.wlv(id);
    return statGrid(skillStats(sim, id, c.level, false), cur > 0 ? skillStats(sim, id, cur, false) : null);
  }
  if (c.kind === 'evolve') { const id = c.id as WeaponId; return statGrid(skillStats(sim, id, MAX_WEAPON_LEVEL, true), skillStats(sim, id, sim.wlv(id), false)); }
  if (c.kind === 'passive') { const id = c.id as PassiveId, cur = sim.plv(id); return statGrid(passiveStats(sim, id, c.level), cur > 0 ? passiveStats(sim, id, cur) : null); }
  if (c.kind === 'fusion' && isFusionId(c.id)) return fusionStatGrid(sim, c.id); // SKILL FUSIONS
  return '';
}
/** one-line stats for a tooltip (HUD slot title) */
const statLine = (st: SkillStats) => st.rows.map((r) => `${r.label} ${fmtRow(r)}`).join(' · ');

/** LEVEL-UP MOMENT: the glint sweep / accent pulse layer of a draft card (animated only under #levelup.rv-full) */
const GLINT = '<i class="lu-glint" aria-hidden="true"></i>';

const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;
/** write text only when it changed (no DOM churn every frame during combat) */
const setText = (el: HTMLElement, t: string) => { if (el.textContent !== t) el.textContent = t; };


/** Surge bar colour = the form you will transform into (index = tier; 0 = base blue): each form's flame from surgeForms.ts,
 *  and a darkened copy for the gradient's near end. Tiers 1-3 keep the gold / crimson / azure the bar had before. */
const hex6 = (c: number) => `#${c.toString(16).padStart(6, '0')}`;
const dark = (c: number, k = 0.55) => hex6(((Math.round(((c >> 16) & 255) * k) << 16) | (Math.round(((c >> 8) & 255) * k) << 8) | Math.round((c & 255) * k)) >>> 0);
const SURGE_BAR = ['#4ab8ff', ...SURGE_FORMS.map((f) => hex6(f.hud ?? f.inner))];
const SURGE_BAR_DARK = ['#1d5c9a', ...SURGE_FORMS.map((f) => dark(f.outer))];
/** 'I · RADIANT' … 'VIII · SHATTERLIGHT' (index = tier) */
const SURGE_LABEL = ['', ...SURGE_FORMS.map((f) => `${f.short} · ${f.name.toUpperCase()}`)];

export class Hud {
  private kiTier = -1;
  private hp = $('#hp-fill'); private hpText = $('#hp-text');
  private xp = $('#xp-fill'); private lvl = $('#level');
  private ki = $('#ki-fill'); private kiText = $('#ki-text');
  private timer = $('#timer'); private waveEl = $('#wave'); private kills = $('#kills'); private shards = $('#shards');
  private weapons = $('#weapons'); private traits = $('#traits');
  private heroHp = $('#herohp'); private heroHpFill = $('#herohp-fill');
  private bossBar = $('#boss'); private bossFill = $('#boss-fill');
  private toastEl = $('#toast');
  private dmgLayer = $('#dmg');
  private abil = { beam: $('#ab-beam'), tf: $('#ab-tf') };
  private kiBar = $('.bar.ki');
  private tBtn = $('#t-tf'); private tPct = $('#tf-pct'); private ringKey = '';
  private slotCount = $('#slotcount');
  private sealsEl = $('#seals');
  private lastSeals = -1;
  private dmgPool: HTMLDivElement[] = [];
  private dmgFlip = new Uint8Array(48);
  private dmgIdx = 0;
  private toastT = 0;
  private lastWeaponsKey = '';
  vignette = $('#vignette');
  /** Storm Lattice SKY STRIKE screen-edge flicker (created on first use) */
  private edgeEl: HTMLDivElement | null = null; private edgeT = 0; private edgeK = 0;
  /** a big lightning strike: flicker the screen edges (k = the FLASH budget's multiplier; 0 = dropped) */
  stormEdge(k: number): void {
    if (k <= 0.02) return;
    if (!this.edgeEl) { this.edgeEl = document.createElement('div'); this.edgeEl.id = 'stormedge'; document.body.appendChild(this.edgeEl); }
    this.edgeT = 0.22; this.edgeK = k;
  }
  /** controller face-button label for Surge (north button): 'Y' on Xbox layouts, '△' on PlayStation */
  padSurge = 'Y';
  /** called when a Surge level becomes ready (and as a reminder while it sits unused); main plays the chime */
  onSurgeReady: ((tier: number) => void) | null = null;
  private surgeReadyTier = 0; private surgeNag = 0;
  /** MAP PICKUPS: Fervor timer chip; chest reveal state */
  private fervor = $('#fervor'); private fervorT = $('#fervor-t'); private fervorFill = $('#fervor-fill'); private fervorOn = false;
  private momEl = $('#momentum'); private momV = $('#momentum-v'); private momFill = $('#momentum-fill'); private momOn = false; // Momentum trait chip
  private chestEl = $('#chest'); private chestCards: HTMLElement[] = []; private chestT = 0; private chestShown = 0;
  /** seconds per upgrade turning over, and the hold after the last one before the run resumes on its own */
  static readonly CHEST_STEP = 0.42; static readonly CHEST_HOLD = 1.1;
  /** ENDLESS GOALS: tier readout under the clock, milestone banner */
  private etEl = $('#endless-tier'); private etTier = $('#et-tier'); private etName = $('#et-name'); private etNext = $('#et-next'); private etFill = $('#et-fill'); private etOn = false;
  private msEl = $('#milestone'); private msT = 0;
  static readonly MILESTONE_SHOW = 3.4;

  /** SURGE FORMS: the 8 segments along the bottom of the Surge bar, one per form in its colour (lit = charged) */
  private kiSegs: HTMLElement[] = [];
  private segKey = '';
  constructor() {
    for (let i = 0; i < 48; i++) { const d = document.createElement('div'); d.className = 'dmg'; this.dmgLayer.appendChild(d); this.dmgPool.push(d); }
    const strip = document.createElement('div'); strip.id = 'ki-segs'; strip.setAttribute('aria-hidden', 'true');
    strip.style.cssText = 'position:absolute;left:4px;right:4px;bottom:1px;height:3px;display:flex;gap:2px;pointer-events:none';
    for (const f of SURGE_FORMS) {
      const seg = document.createElement('i'); seg.className = 'ki-seg'; seg.dataset.tier = String(f.tier); seg.title = `${f.short} · ${f.name}`;
      seg.style.cssText = `flex:1;border-radius:2px;background:${hex6(f.hud ?? f.inner)};opacity:.18;transition:opacity .2s`;
      strip.appendChild(seg); this.kiSegs.push(seg);
    }
    this.kiBar.appendChild(strip);
  }

  update(sim: Sim, dt: number): void {
    const p = sim.player;
    this.hp.style.width = `${(p.hp / p.maxHp) * 100}%`;
    this.heroHpFill.style.width = `${Math.max(0, p.hp / p.maxHp) * 100}%`;
    setText(this.hpText, `${Math.ceil(p.hp)} / ${p.maxHp}`);
    this.xp.style.width = `${(p.xp / p.xpNext) * 100}%`;
    setText(this.lvl, `LV ${p.level}`);
    const tf = sim.transformed;
    const hasSurge = sim.weapons.has('surge'), hasLance = sim.weapons.has('lance');
    // staged Surge bar (owner 2026-11-01): fill yellow first, then charge again in red, then blue (up to the unlocked level)
    const kiReady = p.ki >= 100 && sim.kiOpen, roman = SURGE_LABEL;
    const maxT = Math.max(1, sim.maxSurgeTier), charged = sim.chargedTier;
    const full = !tf && charged >= maxT;
    const fillTier = tf ? sim.surgeTier : full ? maxT : charged + 1;            // the level currently filling
    const underTier = tf || full ? 0 : charged;                                 // completed level shown beneath
    const kiKey = fillTier * 10 + underTier;
    if (kiKey !== this.kiTier) {
      this.kiTier = kiKey;
      this.ki.style.setProperty('--ki', SURGE_BAR[fillTier] ?? SURGE_BAR[1]); this.ki.style.setProperty('--ki-dark', SURGE_BAR_DARK[fillTier] ?? SURGE_BAR_DARK[1]);
      this.kiBar.style.background = underTier ? `linear-gradient(90deg, ${SURGE_BAR_DARK[underTier]}, ${SURGE_BAR[underTier]})` : '';
    }
    this.kiBar.style.display = hasSurge ? '' : 'none';
    { // SURGE FORMS: segments — charged forms lit, the form filling now half-lit, locked forms hidden
      const sk = `${maxT}|${charged}|${tf ? sim.surgeTier : 0}`;
      if (sk !== this.segKey) {
        this.segKey = sk;
        this.kiSegs.forEach((seg, i) => { const t = i + 1; seg.style.display = t <= maxT ? '' : 'none'; seg.style.opacity = tf ? (t <= sim.surgeTier ? '1' : '.18') : t <= charged ? '1' : t === charged + 1 ? '.5' : '.18'; });
      }
    }
    this.ki.style.width = tf ? `${(p.transformT / sim.surgeDuration) * 100}%` : full ? '100%' : sim.surgeCdT > 0 ? '0%' : `${sim.kiStageFrac * 100}%`;
    this.ki.classList.toggle('full', full || tf); // glow only when fully charged, so the completed stage stays visible beneath
    const how = document.body.classList.contains('touch') ? 'TAP SURGE' : document.body.classList.contains('pad') ? `PRESS ${this.padSurge}` : 'PRESS R';
    const pips = maxT > 1 ? ' ' + Array.from({ length: maxT }, (_, i) => (i < charged ? '◆' : '◇')).join('') : '';
    // Surge ready notice (owner: "not obvious"): banner + chime when a level becomes ready, the bar and button blink
    // until used, and a gentle reminder every 12 s while it stays unused
    const readyNow = !tf && charged > 0 && sim.kiOpen;
    // SURGE BATTERY: mid-Surge step-up to a higher charged form
    const stepUp = tf && sim.plv('battery') > 0 && charged > sim.surgeTier;
    if (readyNow && charged > this.surgeReadyTier) {
      this.surgeNag = 0; this.toast(`${surgeForm(charged)?.name.toUpperCase() ?? 'SURGE'} READY — ${how}`, `surge t${charged}`, 2.4); this.onSurgeReady?.(charged);
    } else if (readyNow && (this.surgeNag += dt) > 12) {
      this.surgeNag = 0; this.toast(`SURGE READY — ${how}`, `surge t${charged}`, 1.6);
    }
    this.surgeReadyTier = readyNow ? charged : 0;
    this.kiBar.classList.toggle('ready', readyNow); this.kiBar.dataset.tier = String(charged);
    this.abil.tf.classList.toggle('blink', readyNow || stepUp); this.abil.tf.dataset.tier = String(charged);
    setText(this.kiText, tf ? (stepUp ? `SURGE ${roman[sim.surgeTier]} · ${roman[charged]} READY — ${how}` : `SURGE ${roman[sim.surgeTier]}`)
      : charged > 0 ? `${roman[charged]} READY — ${how}${pips}`
      : sim.surgeCdT > 0 && hasSurge ? `SURGE COOLDOWN ${Math.ceil(sim.surgeCdT)}s${pips}`
      : `SURGE ${Math.floor(sim.kiStageFrac * 100)}%${pips}`);
    this.abil.beam.classList.toggle('off', !hasLance);
    this.abil.tf.classList.toggle('off', !hasSurge);
    this.tBtn.classList.toggle('off', !hasSurge);
    this.tBtn.classList.toggle('ready', kiReady || readyNow || stepUp);
    this.tBtn.classList.toggle('active', tf);
    // touch Surge button = a ring that fills as the current stage charges (owner 2026-11-02: the bar merged into the button);
    // the completed stage stays visible as the ring's track colour, like the bar's under-fill
    if (hasSurge) {
      const frac = tf ? p.transformT / sim.surgeDuration : full ? 1 : sim.surgeCdT > 0 ? 0 : sim.kiStageFrac;
      const label = stepUp ? 'STEP UP' : tf ? 'ACTIVE' : readyNow ? 'READY' : sim.surgeCdT > 0 ? `${Math.ceil(sim.surgeCdT)}s` : `${Math.floor(sim.kiStageFrac * 100)}%`;
      const rk = `${Math.round(frac * 200)}|${fillTier}|${underTier}|${label}`;
      if (rk !== this.ringKey) {
        this.ringKey = rk;
        this.tBtn.style.setProperty('--p', frac.toFixed(3));
        this.tBtn.style.setProperty('--ring', SURGE_BAR[fillTier] ?? SURGE_BAR[1]);
        this.tBtn.style.setProperty('--track', underTier ? SURGE_BAR[underTier] : 'rgba(255,255,255,.14)');
        setText(this.tPct, label);
      }
    }
    this.tBtn.dataset.tier = String(readyNow ? charged : sim.surgeTier);
    const wl = sim.endless ? `ENDLESS · WAVE ${sim.wave}` : sim.wave > 0 ? `WAVE ${sim.wave} · ${sim.waveName}` : '';
    if (this.waveEl.textContent !== wl) this.waveEl.textContent = wl;
    const t = Math.floor(sim.time); setText(this.timer, `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`);
    setText(this.kills, `${sim.kills}`);
    setText(this.shards, `${sim.shards}`);
    if (sim.seals !== this.lastSeals) {
      this.lastSeals = sim.seals;
      let h = ''; for (let i = 0; i < SEALS_REQUIRED; i++) h += `<i class="${i < sim.seals ? 'on' : ''}"></i>`;
      this.sealsEl.innerHTML = `<span class="slbl">SEALS</span><span class="sd">${h}</span>`; // a strip inside the status card
      this.sealsEl.title = 'Array Seals: elites drop them. Collect 6 for a Convergence (bonus boon pick).';
    }
    // abilities
    this.cd(this.abil.beam, p.beamT > 0 || p.beamCharge > 0 ? 1 : p.beamCd / sim.beamCdMax);
    this.cd(this.abil.tf, tf ? 0 : 1 - Math.min(100, p.ki) / 100);
    this.abil.tf.classList.toggle('ready', p.ki >= 100 && !tf);
    this.abil.beam.classList.toggle('ready', p.beamCd <= 0 && p.beamT <= 0);
    // weapons
    const key = [...sim.weapons].map(([k, v]) => k + v + (sim.evolved.has(k) ? 'E' : '')).join() + [...sim.passives].map(([k, v]) => k + v).join() + [...sim.fusion.owned.keys()].join();
    if (key !== this.lastWeaponsKey) {
      this.lastWeaponsKey = key;
      let html = '', n = 0;
      for (const [id, lv] of sim.weapons) {
        if (INNATE.includes(id)) continue;
        const evo = sim.evolved.has(id); n++;
        html += `<div class="slot ${evo ? 'evo' : ''}" data-w="${id}" title="${evo ? WEAPONS[id].evolvedName : WEAPONS[id].name} — ${statLine(skillStats(sim, id, lv, evo))}"><span>${techIcon(id, evo)}</span><i>${evo ? '★' : lv}</i></div>`;
      }
      for (const f of sim.fusion.owned.keys()) { n++; html += fusionSlot(sim, f).replace('<div class="slot', `<div data-f="${f}" class="slot`); } // SKILL FUSIONS: one slot each (data-f: TRAINING taps)
      // owner 2026-11-02: only the filled slots, plus a small count (skills · passives) instead of 12 empty squares
      this.weapons.innerHTML = html;
      let th = ''; let m = 0;
      for (const [id, lv] of sim.passives) { if (lv <= 0) continue; m++; th += `<div class="slot passive" title="${PASSIVES[id].name} — ${statLine(passiveStats(sim, id, lv))}"><span>${traitIcon(id)}</span><i>${lv}</i></div>`; }
      this.traits.innerHTML = th;
      this.slotCount.innerHTML = `<b class="sk">${n}/${sim.skillSlots}</b><span>·</span><b class="ps">${m}/${MAX_PASSIVES}</b>`;
    }
    // boss
    if (sim.boss && sim.boss.alive) {
      if (!this.bossBar.classList.contains('on')) this.bossBar.classList.add('on'); // layout (block / inline row) is the stylesheet's job
      // BOSS FORMS (owner 2026-11-02): "NAME — FORM" once it has transformed, ticks at the transformation thresholds
      const fi = sim.bossFormInfo(sim.boss);
      const nm = fi && fi.form > 0 ? `${sim.bossName} — ${fi.name}` : sim.bossName;
      const nEl = this.bossBar.querySelector('.name') as HTMLElement | null; if (nEl && nEl.textContent !== nm) nEl.textContent = nm;
      this.bossFill.style.width = `${(sim.boss.hp / sim.boss.maxHp) * 100}%`;
      this.bossGates(fi ? fi.form : -1, fi?.accent ?? 0, !!fi && fi.transforming > 0);
    } else if (sim.heraldBar) { // THE NULL HERALD: its health on the boss bar while no other boss is active
      const h = sim.heraldBar;
      if (!this.bossBar.classList.contains('on')) this.bossBar.classList.add('on');
      const nEl = this.bossBar.querySelector('.name') as HTMLElement | null; if (nEl && nEl.textContent !== 'NULL HERALD') nEl.textContent = 'NULL HERALD';
      this.bossFill.style.width = `${(h.hp / h.maxHp) * 100}%`;
      this.bossGates(-1, 0, false);
    } else if (this.bossBar.classList.contains('on')) this.bossBar.classList.remove('on');
    // MAP PICKUPS: Fervor (+damage for a few seconds) shows as a draining chip under the clock
    const fury = sim.loot.furyT;
    if ((fury > 0) !== this.fervorOn) { this.fervorOn = fury > 0; this.fervor.hidden = !this.fervorOn; }
    if (this.fervorOn) { setText(this.fervorT, String(Math.ceil(fury))); this.fervorFill.style.transform = `scaleX(${(fury / FURY_TIME).toFixed(3)})`; }
    // Momentum trait (group A, owner 2026-11-03): a chip like Fervor's, shown while the trait is held; the bar is the build-up
    const momOn = sim.plv('momentum') > 0;
    if (momOn !== this.momOn) { this.momOn = momOn; this.momEl.hidden = !momOn; }
    if (momOn) { setText(this.momV, `+${Math.round(sim.momentumBonus * 100)}%`); this.momFill.style.transform = `scaleX(${sim.momentumK.toFixed(3)})`; }
    // ENDLESS GOALS: current tier + countdown to the next milestone (compact; the name drops out on phones)
    if (sim.endless !== this.etOn) { this.etOn = sim.endless; this.etEl.hidden = !this.etOn; }
    if (this.etOn) {
      const n = sim.endlessTier, left = sim.nextMilestoneIn, a = endlessMilestoneAt(n), b = endlessMilestoneAt(n + 1);
      setText(this.etTier, n ? `TIER ${romanNum(n)}` : 'ENDLESS'); setText(this.etName, n ? endlessTierName(n) : '');
      const s = Math.ceil(left); setText(this.etNext, `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);
      this.etFill.style.transform = `scaleX(${Math.max(0, Math.min(1, 1 - left / (b - a))).toFixed(3)})`;
      this.etEl.title = `${n ? endlessTierLabel(n) : 'Endless'} · next: ${endlessTierLabel(n + 1)} in ${this.etNext.textContent}`;
    }
    if (this.msT > 0) { this.msT -= dt; if (this.msT <= 0) this.msEl.classList.remove('show'); }
    // toast
    if (this.toastT > 0) { this.toastT -= dt; if (this.toastT <= 0) this.toastEl.classList.remove('show'); }
    // Storm Lattice SKY STRIKE: an electric-violet screen-edge flicker (Reduce flashing: one steady, dimmer fade, no flicker)
    if (this.edgeT > 0) {
      this.edgeT = Math.max(0, this.edgeT - dt);
      const u = this.edgeT / 0.22, fl = FLASH.mode === 'off' ? (Math.random() < 0.35 ? 0.35 : 1) : 1;
      this.edgeEl!.style.opacity = (0.55 * this.edgeK * u * fl).toFixed(3);
    }
    // hurt flash + low-HP pulse (a11y: both follow Reduce flashing; the cue stays, its strength and pulse depth drop)
    this.vignette.style.opacity = String(Math.max(p.hurtT * 2.5 * (0.3 + 0.7 * FLASH.dim), p.hp / p.maxHp < 0.3 ? 0.35 + Math.sin(performance.now() / 200) * FLASH.flick(0.15) : 0));
  }

  /** Small HP bar under the hero (screen px of the hero's feet). */
  placeHeroHp(x: number, y: number, visible: boolean): void {
    this.heroHp.style.display = visible ? '' : 'none';
    if (visible) this.heroHp.style.transform = `translate(${Math.round(x - 22)}px, ${Math.round(y + 6)}px)`;
  }

  private cd(el: HTMLElement, frac: number): void {
    (el.querySelector('.cd') as HTMLElement).style.height = `${Math.max(0, Math.min(1, frac)) * 100}%`;
  }

  private gateEls: HTMLElement[] = []; private gateKey = '';
  /** BOSS FORMS: threshold ticks on the boss bar (FORM_GATES; a passed gate dims), the fill takes the form's accent, and the
   *  bar glows while the boss transforms. Inline styles (the stylesheet belongs to the menu / theme work). */
  private bossGates(form: number, accent: number, transforming: boolean): void {
    const key = `${form}|${accent}|${transforming}`; if (key === this.gateKey) return; this.gateKey = key;
    const bar = this.bossFill.parentElement as HTMLElement | null; if (!bar) return;
    if (!this.gateEls.length) {
      bar.style.position = 'relative';
      for (const g of FORM_GATES) {
        const i = document.createElement('i');
        Object.assign(i.style, { position: 'absolute', top: '0', bottom: '0', left: `${g * 100}%`, width: '2px', marginLeft: '-1px', background: '#fff', boxShadow: '0 0 4px #fff, 0 0 1px #000', pointerEvents: 'none' });
        bar.appendChild(i); this.gateEls.push(i);
      }
    }
    this.gateEls.forEach((el, k) => { el.style.display = form < 0 ? 'none' : ''; el.style.opacity = form > k ? '0.3' : '0.95'; });
    const hex = '#' + accent.toString(16).padStart(6, '0');
    this.bossFill.style.background = form > 0 ? `linear-gradient(90deg, #401030, ${hex})` : '';
    bar.style.boxShadow = transforming ? `0 0 12px ${hex}, 0 0 4px #fff` : '';
  }

  toast(text: string, cls = '', dur = 2.2): void {
    // a Surge-ready banner isn't overwritten by routine toasts (e.g. every Nova Lance shot); warnings still win
    if (this.toastEl.classList.contains('surge') && this.toastT > 0.5 && !cls.includes('surge') && !cls.includes('warn')) return;
    this.toastEl.textContent = text; this.toastEl.className = `show ${cls}`; this.toastT = dur;
  }

  damageNumber(x: number, y: number, dmg: number, crit: boolean): void {
    const i = this.dmgIdx, d = this.dmgPool[i]; this.dmgIdx = (i + 1) % this.dmgPool.length;
    d.textContent = String(Math.round(dmg));
    // PERF-STRESS (2026-11-02): restart the float-up animation by alternating two identical keyframe names instead of
    // forcing a synchronous layout (`void offsetWidth`) per number — up to 14 forced reflows per sim step in big fights
    const f = this.dmgFlip[i] ^= 1;
    d.className = (crit ? 'dmg crit ' : 'dmg ') + (f ? 'go' : 'go2');
    d.style.left = `${x + (Math.random() - 0.5) * 20}px`; d.style.top = `${y}px`;
  }

  /** LEVEL-UP MOMENT: clears the reveal classes once every reveal animation is over */
  private rvTimer = 0;
  /** @param reveal LEVEL-UP MOMENT card reveal for a freshly opened draft (src/app/levelUpSeq.ts); omitted on re-renders
   *  (selection / reroll / ban), which never replay it — they also end a reveal still playing (an input skips it) */
  showLevelUp(choices: Choice[], sel: number, sim?: Sim, banArmed = false, reveal?: RevealKind): void {
    const el = $('#levelup');
    const RV = ['rv-full', 'rv-quick', 'rv-fade'];
    if (reveal && reveal !== 'none') {
      if (RV.some((c) => el.classList.contains(c))) { el.classList.remove(...RV); void el.offsetWidth; } // restart the title stamp
      el.classList.add(`rv-${reveal}`);
      clearTimeout(this.rvTimer); this.rvTimer = window.setTimeout(() => el.classList.remove(...RV), LU_REVEAL_MS + 150);
    } else el.classList.remove(...RV);
    el.classList.add('show');
    setText($('#lu-title'), sim?.draftTag === 'relic' ? 'RELIC CACHE' : sim?.draftTag === 'fusion' ? 'CHEST · FUSION READY' : 'LEVEL UP'); // MAP PICKUPS: a relic cache's free pick / a chest's FUSION offer
    setText($('#lu-mult'), sim && sim.pendingLevels > 1 ? `×${sim.pendingLevels}` : ''); // LEVEL-UP MOMENT: drafts still queued, this one included
    if (sim) {
      $('#n-reroll').textContent = String(sim.rerolls); $('#n-ban').textContent = String(sim.bans);
      ($('#btn-reroll') as HTMLButtonElement).disabled = sim.rerolls <= 0;
      ($('#btn-ban') as HTMLButtonElement).disabled = sim.bans <= 0;
      $('#btn-ban').classList.toggle('armed', banArmed);
      $('#draft-tools').style.display = sim.rerolls + sim.bans > 0 || banArmed ? '' : 'none';
    }
    const cards = choices.map((c, i) => {
      const req = sim ? this.requirement(c, sim) : '';
      const isNew = (c.kind === 'weapon' || c.kind === 'passive') && c.level === 1;
      if (c.kind === 'fusion' && isFusionId(c.id)) // SKILL FUSIONS: the emerald FUSION card (partner icons → glowing arrow → result)
        return `<button class="card fusion ${i === sel ? 'sel' : ''}" data-i="${i}" style="--i:${i}">${GLINT}<div class="icon fu-icon">${fusionCardIcon(sim, c.id)}</div><div class="head"><h3>✦ ${c.name}</h3><span class="tag fu">FUSION</span></div><div class="kind">FUSION · TWO SKILLS BECOME ONE</div><p>${c.desc}</p>${sim ? cardStats(c, sim) + fusionCardNote(sim, c.id) : ''}<kbd>${i + 1}</kbd></button>`;
      const tag = c.kind === 'evolve' ? 'EVOLUTION' : isNew ? 'NEW' : c.kind === 'weapon' || c.kind === 'passive' ? `LV ${c.level}` : c.kind === 'bonus' ? 'BONUS' : 'RESTORE';
      const kindLbl = c.kind === 'weapon' || c.kind === 'evolve' ? 'SKILL' : c.kind === 'passive' ? (riskCls(c.kind, c.id) ? 'TRAIT · RISK' : 'TRAIT') : c.kind === 'bonus' ? (c.id.startsWith('w:') || c.id.startsWith('g:') ? 'ATTUNEMENT' : 'MAX BUILD REWARD') : '';
      const bonusGlyph: Record<string, string> = { recover: '✚', cache: '◆', 'g:might': '✦', 'g:haste': '↻', 'g:reach': '◎', 'g:fleet': '»', 'g:vigor': '♥' };
      const icon = c.kind === 'weapon' || c.kind === 'evolve' ? techIcon(c.id as WeaponId, c.kind === 'evolve')
        : c.kind === 'passive' ? traitIcon(c.id as PassiveId)
        : c.kind === 'bonus' ? (c.id.startsWith('w:') && isFusionId(c.id.slice(2)) ? fusionIcon(c.id.slice(2) as FusionId) : c.id.startsWith('w:') ? techIcon(c.id.slice(2) as WeaponId, sim?.evolved.has(c.id.slice(2) as WeaponId)) : c.id === 'recover' ? HEAL_ICON : `<span class="bglyph">${bonusGlyph[c.id] ?? '✦'}</span>`)
        : HEAL_ICON;
      return `<button class="card ${c.kind}${riskCls(c.kind, c.id)} ${i === sel ? 'sel' : ''}" data-i="${i}" style="--i:${i}">${GLINT}<div class="icon">${icon}</div><div class="head"><h3>${c.name}</h3><span class="tag ${isNew ? 'new' : ''}">${tag}</span></div><div class="kind">${kindLbl}</div><p>${c.desc}</p>${sim ? cardStats(c, sim) : ''}${req}<kbd>${i + 1}</kbd></button>`;
    }).join('');
    $('#cards').innerHTML = cards;
    if (sim) { $('#lu-loadout').innerHTML = this.lattice(sim, choices, 'slots'); $('#lu-bonds').innerHTML = this.lattice(sim, choices, 'bonds'); }
  }
  hideLevelUp(): void { $('#levelup').classList.remove('show'); }

  /** MAP PICKUPS chest reveal (the run is paused by main.ts while it shows). The upgrades were already applied in order by
   *  the sim; here they turn over one by one, then the popup holds briefly and closes itself (tickChest returns true). */
  showChest(picks: Choice[], boss: boolean, sim: Sim, hint: string): void {
    const n = picks.length;
    $('#chest-title').textContent = `${boss ? 'BOSS CHEST' : 'CHEST'} · ${n} UPGRADE${n > 1 ? 'S' : ''}`;
    this.chestEl.classList.toggle('boss', boss);
    const box = $('#chest-cards');
    box.innerHTML = picks.map((c) => {
      const isNew = (c.kind === 'weapon' || c.kind === 'passive') && c.level === 1;
      if (c.kind === 'fusion' && isFusionId(c.id)) // SKILL FUSIONS: offered on the draft that follows (take or skip)
        return `<div class="ccard fusion"><div class="icon">${fusionIcon(c.id)}</div><h3>✦ ${c.name}</h3><span class="tag">FUSION · TAKE OR SKIP</span></div>`;
      const tag = c.kind === 'evolve' ? 'EVOLUTION' : isNew ? 'NEW' : c.kind === 'weapon' || c.kind === 'passive' ? `LV ${c.level}` : 'BONUS';
      const icon = c.kind === 'weapon' || c.kind === 'evolve' ? techIcon(c.id as WeaponId, c.kind === 'evolve') : c.kind === 'passive' ? traitIcon(c.id as PassiveId) : '<span class="bglyph">◆</span>';
      return `<div class="ccard ${c.kind}${riskCls(c.kind, c.id)}"><div class="icon">${icon}</div><h3>${c.name}</h3><span class="tag ${isNew ? 'new' : ''}">${tag}</span></div>`;
    }).join('');
    this.chestCards = Array.from(box.children) as HTMLElement[];
    this.chestT = 0; this.chestShown = 0;
    $('#chest-hint').textContent = hint;
    this.chestEl.classList.add('show');
    void sim;
  }
  /** advance the reveal by real seconds; skip = show everything now. Returns true when the popup is done. */
  tickChest(dt: number, skip = false): boolean {
    const n = this.chestCards.length;
    if (skip && this.chestShown >= n) return true; // everything is face up: a confirm closes it
    this.chestT = skip && this.chestShown < n ? Hud.CHEST_STEP * n : this.chestT + dt;
    const due = Math.min(n, Math.floor(this.chestT / Hud.CHEST_STEP) + 1);
    while (this.chestShown < due) this.chestCards[this.chestShown++].classList.add('on');
    return this.chestT >= Hud.CHEST_STEP * n + Hud.CHEST_HOLD;
  }
  /** how many cards are face up (main plays a tick per card) */
  get chestRevealed(): number { return this.chestShown; }
  hideChest(): void { this.chestEl.classList.remove('show'); }

  /** Evolution-planning hint shown on a draft card. */
  private requirement(c: Choice, sim: Sim): string {
    if (c.kind === 'weapon') {
      const w = WEAPONS[c.id as WeaponId]; const have = sim.plv(w.evolvesWith) > 0;
      // SKILL FUSIONS: name the fusions this skill can make with a skill already held
      const fz = recipesWith(c.id as WeaponId).filter((f) => !sim.fusion.has(f.id) && sim.weapons.has(f.a === c.id ? f.b : f.a));
      const fh = fz.length ? `<div class="req fu-req">✦ Fuses at rank ${MAX_WEAPON_LEVEL} with ${fz.map((f) => `${WEAPONS[f.a === c.id ? f.b : f.a].name} → ${f.name}`).join(', ')}</div>` : '';
      return `<div class="req ${have ? 'ok' : ''}">Evolves at rank ${MAX_WEAPON_LEVEL} with ${PASSIVES[w.evolvesWith].name} ${have ? '✓' : '(needed)'}</div>${fh}`;
    }
    if (c.kind === 'passive') {
      const partners = [...sim.weapons.keys()].filter((id) => WEAPONS[id].evolvesWith === c.id && !sim.evolved.has(id));
      if (partners.length) return `<div class="req ok">Unlocks evolution: ${partners.map((id) => WEAPONS[id].evolvedName).join(', ')}</div>`;
    }
    return '';
  }

  /** ENDLESS GOALS: the celebratory milestone banner (tier n reached at +at endless seconds, shards banked) */
  milestone(tier: number, name: string, shards: number, at: number): void {
    setText($('#ms-at'), `ENDLESS MILESTONE · +${Math.floor(at / 60)}:${String(Math.floor(at % 60)).padStart(2, '0')}`);
    setText($('#ms-tier'), `TIER ${romanNum(tier)}`); setText($('#ms-name'), name); setText($('#ms-reward'), `+${shards} ◆ BANKED`);
    this.msEl.dataset.tier = String(Math.min(6, tier));
    this.msEl.classList.remove('show'); void this.msEl.offsetWidth; this.msEl.classList.add('show'); // (rare: restart the entrance)
    this.msT = Hud.MILESTONE_SHOW;
  }

  showBoons(sim: Sim, sel: number): void {
    $('#boon').classList.add('show');
    const apex = sim.boonKind === 'apex'; $('#boon').classList.toggle('apex', apex);
    setText($('#boon-title'), apex ? 'APEX BOON' : 'CONVERGENCE');
    setText($('#boon-sub'), apex ? `${endlessTierLabel(sim.endlessTier)} reached. Choose one boon to face what comes next.` : 'Six Array Seals resonate. Choose one boon.');
    if (apex) {
      $('#boon-cards').innerHTML = sim.apexChoices.map((id, i) => {
        const b = APEX_BOONS[id];
        return `<button class="card boonc apexc ${i === sel ? 'sel' : ''}" data-i="${i}"><div class="icon">${b.icon}</div><div class="tag">APEX BOON</div><h3>${b.name}</h3><p>${b.desc}</p><kbd>${i + 1}</kbd></button>`;
      }).join('');
      return;
    }
    $('#boon-cards').innerHTML = sim.boonChoices.map((id: BoonId, i) => {
      const b = BOONS[id];
      return `<button class="card boonc ${i === sel ? 'sel' : ''}" data-i="${i}"><div class="icon">${b.icon}</div><div class="tag">CONVERGENCE BOON</div><h3>${b.name}</h3><p>${b.desc}</p><kbd>${i + 1}</kbd></button>`;
    }).join('');
  }
  hideBoons(): void { $('#boon').classList.remove('show'); }


  /** Bond Lattice: build slots + what each held technique needs to evolve. Shown on pause and level-up.
   *  `offer` highlights bonds that one of the current draft cards would advance. */
  lattice(sim: Sim, offer: Choice[] = [], part: 'all' | 'slots' | 'bonds' = 'all'): string {
    const techs = [...sim.weapons].filter(([id]) => !INNATE.includes(id));
    const traits = [...sim.passives].filter(([, lv]) => lv > 0);
    const offered = (kind: string, id: string) => offer.some((c) => c.id === id && (c.kind === kind || (kind === 'weapon' && c.kind === 'evolve')));
    const pips = (lv: number, max: number) => { let h = ''; for (let i = 0; i < max; i++) h += `<i class="${i < lv ? 'on' : ''}"></i>`; return `<span class="pips">${h}</span>`; };
    const slot = (icon: string, name: string, lv: number, max: number, hi: boolean) => `<div class="ls${hi ? ' hi' : ''}" title="${name}"><b>${icon}</b>${pips(lv, max)}</div>`;
    const empty = (n: number) => '<div class="ls open"><b>+</b></div>'.repeat(Math.max(0, n));
    const fused = [...sim.fusion.owned.keys()]; // SKILL FUSIONS: a fusion fills one slot
    const tRow = techs.map(([id, lv]) => slot(techIcon(id, sim.evolved.has(id)), WEAPONS[id].name, sim.evolved.has(id) ? MAX_WEAPON_LEVEL : lv, MAX_WEAPON_LEVEL, offered('weapon', id))).join('')
      + fused.map((f) => slot(fusionIcon(f), FUSIONS[f].name, MAX_WEAPON_LEVEL, MAX_WEAPON_LEVEL, offered('fusion', f))).join('') + empty(sim.skillSlots - techs.length - fused.length);
    const pRow = traits.map(([id, lv]) => slot(traitIcon(id), PASSIVES[id].name, lv, PASSIVES[id].max ?? 5, offered('passive', id))).join('') + empty(MAX_PASSIVES - traits.length);
    const traitFull = traits.length >= MAX_PASSIVES;
    const bonds = [...sim.weapons].map(([id, lv]) => {
      const w = WEAPONS[id]; const pid = w.evolvesWith; const plv = sim.plv(pid);
      const evo = sim.evolved.has(id);
      const rankOk = lv >= MAX_WEAPON_LEVEL, traitOk = plv > 0;
      let state: string, cls: string;
      if (evo) { state = 'EVOLVED'; cls = 'done'; }
      else if (rankOk && traitOk) { state = 'READY'; cls = 'ready'; }
      else if (!traitOk && traitFull) { state = 'NO TRAIT SLOT'; cls = 'blocked'; }
      else { state = `${rankOk ? '' : `rank ${lv}/${MAX_WEAPON_LEVEL}`}${!rankOk && !traitOk ? ' · ' : ''}${traitOk ? '' : `needs ${PASSIVES[pid].name}`}`; cls = 'wait'; }
      const hi = offered('weapon', id) || offered('passive', pid);
      return `<div class="bond ${cls}${hi ? ' hi' : ''}">`
        + `<span class="bt ${rankOk || evo ? 'ok' : ''}">${techIcon(id, evo)}<em>${evo ? '★' : lv}</em></span>`
        + `<span class="plus">+</span>`
        + `<span class="bt ${traitOk ? 'ok' : 'miss'}">${traitIcon(pid)}</span>`
        + `<span class="arrow">➜</span>`
        + `<span class="bn"><strong>${w.evolvedName}</strong><small>${state}</small></span></div>`;
    }).join('');
    const slots = `<div class="lat-rows">`
      + `<div class="lat-row"><span class="lbl">SKILL ${techs.length + fused.length}/${sim.skillSlots}</span>${tRow}</div>`
      + `<div class="lat-row"><span class="lbl">TRAIT ${traits.length}/${MAX_PASSIVES}</span>${pRow}</div></div>`;
    const fb = fusionBonds(sim); // SKILL FUSIONS
    const bondHtml = `<div class="lat-head">BOND LATTICE · EVOLUTIONS</div><div class="bonds">${bonds}</div>${fb}`;
    return part === 'slots' ? slots : part === 'bonds' ? bondHtml : `<div class="lat-head">BOND LATTICE</div>${slots}<div class="bonds">${bonds}</div>${fb}`;
  }

  /** Pause-screen build (owner 2026-11-02 UI pass: "dark card with a coloured accent border and plain high-contrast white
   *  text"): one card per held skill / passive with rank pips, the evolution plan in words, the stat chips and the share of
   *  damage dealt; the free slots are one compact row of small empty chips under each section. */
  planner(sim: Sim): void {
    const total = Object.values(sim.dmgBySrc).reduce((a, b) => a + b, 0) || 1;
    const pips = (lv: number, max: number) => { let h = ''; for (let i = 0; i < max; i++) h += `<i class="${i < lv ? 'on' : ''}"></i>`; return `<span class="bp-pips" aria-label="rank ${lv} of ${max}">${h}</span>`; };
    const free = (n: number, what: string) => n <= 0 ? '' : `<div class="bp-free"><span class="bp-chips">${'<i></i>'.repeat(n)}</span><span>${n} free ${what} slot${n === 1 ? '' : 's'}</span></div>`;
    const techs = [...sim.weapons].filter(([id]) => !INNATE.includes(id) || id === 'surge');
    const traits = [...sim.passives].filter(([, lv]) => lv > 0);
    const tCards = techs.map(([id, lv]) => {
      const w = WEAPONS[id]; const evo = sim.evolved.has(id); const hasP = sim.plv(w.evolvesWith) > 0;
      const ready = !evo && lv >= MAX_WEAPON_LEVEL && hasP;
      const plan = evo ? '★ Evolved' : `Evolves into <b>${w.evolvedName}</b> · ${hasP ? `<span class="ok">✓ ${PASSIVES[w.evolvesWith].name}</span>` : `needs <span class="need">${PASSIVES[w.evolvesWith].name}</span>`}${ready ? ' · <span class="ok">ready at the next level-up</span>' : ''}`;
      const d = sim.dmgBySrc[id] ?? 0;
      const dmg = d > 0 ? `<p class="bp-dmg">Damage dealt: <b>${Math.round(d).toLocaleString()}</b> (${Math.round((d / total) * 100)}% of total)</p>` : ''; // non-damage skills (Surge Form) show no line
      return `<div class="bp-card ${evo ? 'evolve' : 'weapon'}${ready ? ' ready' : ''}"><div class="bp-ico">${techIcon(id, evo)}</div>`
        + `<div class="bp-txt"><div class="bp-h"><h3>${evo ? w.evolvedName : w.name}</h3>${pips(evo ? MAX_WEAPON_LEVEL : lv, MAX_WEAPON_LEVEL)}</div><p class="bp-plan">${plan}</p>${statGrid(skillStats(sim, id, lv, evo))}${dmg}</div></div>`;
    }).join('');
    const pCards = traits.map(([id, lv]) => `<div class="bp-card passive${riskCls('passive', id)}"><div class="bp-ico">${traitIcon(id)}</div>`
      + `<div class="bp-txt"><div class="bp-h"><h3>${PASSIVES[id].name}</h3>${pips(lv, PASSIVES[id].max ?? 5)}</div><p class="bp-plan">${PASSIVES[id].desc}</p>${statGrid(passiveStats(sim, id, lv))}</div></div>`).join('');
    const fCards = [...sim.fusion.owned.keys()].map((f) => fusionPlannerCard(sim, f, total)).join(''); // SKILL FUSIONS
    const nT = techs.filter(([id]) => !INNATE.includes(id)).length + sim.fusion.owned.size;
    const extra = sim.revives ? ` · Revives ${sim.revives}` : '';
    $('#planner').innerHTML = `<div class="bp-sec">SKILLS <small>${nT}/${sim.skillSlots}</small></div><div class="bp-grid">${fCards}${tCards}</div>${free(sim.skillSlots - nT, 'skill')}`
      + `<div class="bp-sec">TRAITS <small>${traits.length}/${MAX_PASSIVES}</small></div>${pCards ? `<div class="bp-grid">${pCards}</div>` : ''}${free(MAX_PASSIVES - traits.length, 'passive')}`
      + `<div class="bp-foot">Seals ${sim.seals}/${SEALS_REQUIRED} · Convergences ${sim.convergences} · Rerolls ${sim.rerolls} · Bans ${sim.bans}${extra}</div>`;
  }


  show(id: string, on: boolean): void { $(id).classList.toggle('show', on); }
}
